import Button from '@components/Button/Button';
import type { EmailSignatureSocialLinkTypes } from '@components/EmailSignaturev2/types';
import Input from '@components/Input/Input';
import { faPencil, faTimes, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNoRenderRef from '@hooks/use-no-render-ref';
import useNode from '@hooks/use-node';
import useOnFocus from '@hooks/use-on-focus';
import useValidateInput, { websiteValidator } from '@hooks/use-validate-input';
import { getURIFromURLFieldValue } from '@modules/card-helpers';
import { Portal } from '@reach/portal';
import { AnimatePresence, motion } from 'motion/react';
import { type FC, type KeyboardEvent, type MouseEvent, useCallback, useState } from 'react';
import type { CardUrlType } from 'types';

import styles from './MobileSocialFieldInputs.module.scss';

const SocialLinkFieldContainer: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['social-link-field-container']}>{children}</div>;
};

const SocialLinkFieldContainerDivider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['social-link-field-container-divider']}>{children}</div>;
};

interface SocialLinkFieldContentProps {
  title: string;
  value?: string;
  linkType: EmailSignatureSocialLinkTypes | 'website';
}
const SocialLinkFieldContent: FC<React.PropsWithChildren<SocialLinkFieldContentProps>> = ({
  title,
  value,
  linkType,
}) => {
  return (
    <div className={styles['social-link-field-content']}>
      <div className={styles['social-link-field-title']}>{title}</div>
      <div className={styles['social-link-field-value']}>
        {getURIFromURLFieldValue(linkType === 'website' ? 'org' : (linkType as CardUrlType), value || '') ??
          value}
      </div>
    </div>
  );
};

interface SocialLinkFieldEditSectionProps {
  mode: 'edit' | 'add';
  onEditButtonClick: () => void;
}
const SocialLinkFieldEditSection: FC<React.PropsWithChildren<SocialLinkFieldEditSectionProps>> = ({
  mode,
  onEditButtonClick,
}) => {
  return (
    <div className={styles['social-link-field-edit-section']}>
      <button onClick={onEditButtonClick} className={styles['edit-button']}>
        {mode === 'add' && 'Add link'}
        {mode === 'edit' && <FontAwesomeIcon icon={faPencil} />}
      </button>
    </div>
  );
};

interface SocialLinkFieldInputsProps {
  show: boolean;
  showDeleteOption: boolean;
  title: string;
  onCloseClick: () => void;
  onSaveButtonClick: () => void;
  onDeleteButtonClick: () => void;
}

const SocialLinkFieldInputs: FC<React.PropsWithChildren<SocialLinkFieldInputsProps>> = ({
  children,
  show,
  showDeleteOption,
  title,
  onCloseClick,
  onSaveButtonClick,
  onDeleteButtonClick,
}) => {
  const [popoverNode, setPopoverNode] = useNode();
  const onCloseClickNoRenderRef = useNoRenderRef(onCloseClick);

  const closePopupOnClick = useCallback(
    (event: MouseEvent) => {
      if (!popoverNode) {
        return;
      }

      if ((event.target as HTMLElement).contains(popoverNode)) {
        onCloseClickNoRenderRef.current();
      }
    },
    [onCloseClickNoRenderRef, popoverNode],
  );

  return (
    <Portal>
      <AnimatePresence>
        {show && (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            ref={setPopoverNode}
            onClick={closePopupOnClick}
            onKeyDown={(event: KeyboardEvent) => {
              if (event.key === 'Escape') {
                onCloseClick();
              }
            }}
            aria-label="Social link edit popup"
            className={styles['social-link-fields-dialog']}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{
                y: '0',
                transition: {
                  damping: 0,
                },
              }}
              exit={{ y: '110%', opacity: 0 }} // 110% instead of 100% to fix iphone issue
              className={styles['social-link-field-inputs']}
            >
              <div className={styles['top-section-container']}>
                <button className={styles['close-buttons']} onClick={onCloseClick}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
                <span className={styles['title']}>{title}</span>
                {showDeleteOption && (
                  <button
                    onClickCapture={onDeleteButtonClick}
                    className={styles['delete-buttons']}
                    onClick={onCloseClick}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
              <div className={styles['social-inputs-container']}>{children}</div>
              <div className={styles['social-inputs-save-btn-container']}>
                <Button size="large" onClick={onSaveButtonClick}>
                  Save
                </Button>
              </div>
            </motion.div>
            )
          </div>
        )}
      </AnimatePresence>
    </Portal>
  );
};

interface SocialFieldInputsPopupImplProps
  extends Omit<SocialLinkFieldInputsProps, 'onSaveButtonClick' | 'showDeleteOption'> {
  currentValue: string;
  onSaveClick: (currentDraftValue: string) => void;
}

const WebsiteFieldInputsPopupImpl: FC<React.PropsWithChildren<SocialFieldInputsPopupImplProps>> = ({
  currentValue,
  onSaveClick,
  onCloseClick,
  onDeleteButtonClick,
  ...rest
}) => {
  const [websiteFieldDraftValue, setWebsiteFieldDraftValue] = useState(currentValue);
  const [websiteInputNode, websiteInputNodeRef] = useNode();
  const [isWebsiteError, errorMessage] = useValidateInput(websiteFieldDraftValue || '', [websiteValidator]);
  const [, , websiteInputBlurred] = useOnFocus(websiteInputNode);

  return (
    <SocialLinkFieldInputs
      {...rest}
      showDeleteOption={!!currentValue}
      onCloseClick={onCloseClick}
      onSaveButtonClick={() => {
        if (isWebsiteError) {
          return;
        }
        if (!websiteFieldDraftValue) {
          onDeleteButtonClick();
        } else {
          onSaveClick(websiteFieldDraftValue);
        }
        onCloseClick();
      }}
      onDeleteButtonClick={() => {
        onDeleteButtonClick();
        setWebsiteFieldDraftValue('');
        onCloseClick();
      }}
    >
      <Input
        ref={websiteInputNodeRef}
        type="url"
        value={websiteFieldDraftValue}
        onChange={setWebsiteFieldDraftValue}
        autoComplete="url"
        label="Website URL"
        errorMessage={errorMessage}
        showError={!!websiteFieldDraftValue && isWebsiteError && websiteInputBlurred}
        enableClearButton
      />
    </SocialLinkFieldInputs>
  );
};

const GenericSocialFieldInputsImpl: FC<React.PropsWithChildren<SocialFieldInputsPopupImplProps>> = ({
  currentValue,
  title,
  onSaveClick,
  onCloseClick,
  onDeleteButtonClick,
  ...rest
}) => {
  const [genericFieldValue, setGenericFieldValue] = useState(currentValue);

  return (
    <SocialLinkFieldInputs
      {...rest}
      showDeleteOption={!!currentValue}
      onSaveButtonClick={() => {
        if (!genericFieldValue) {
          onDeleteButtonClick();
        } else {
          onSaveClick(genericFieldValue);
        }
        onCloseClick();
      }}
      onDeleteButtonClick={() => {
        onDeleteButtonClick();
        setGenericFieldValue('');
        onCloseClick();
      }}
      onCloseClick={onCloseClick}
      title={title}
    >
      <Input
        type="url"
        value={genericFieldValue}
        onChange={setGenericFieldValue}
        autoComplete="url"
        label={'Username/URL'}
        enableClearButton
      />
    </SocialLinkFieldInputs>
  );
};

export {
  SocialLinkFieldContainer,
  SocialLinkFieldContainerDivider,
  SocialLinkFieldContent,
  GenericSocialFieldInputsImpl,
  WebsiteFieldInputsPopupImpl,
  SocialLinkFieldEditSection,
};
